import PropTypes from 'prop-types';
import { useState, useEffect, useMemo } from 'react';

import { getEnv } from '@ctct/ctct-helpers';
import { Dialog, Button, Field, Input, List, Alert } from '@ctct/ui-react';

import DuplicateConfirmation from './DuplicateConfirmation';
import InviteSentDialog from './InviteSentDialog';
import RemoveAUserFirstDialog from './RemoveAUserFirstDialog';
import UpgradeModal from './UpgradeModal';
import AccountSvc from '../../../../api/AccountSvc';
import { getAccountData, getPartnerOwnerSummary } from '../../../../api/DistUI';
import ProductSvc from '../../../../api/ProductSvc';
import SettingsSvc from '../../../../api/SettingsSvc';
import { ACCOUNT_MANAGER, CAMPAIGN_CREATOR, BP_CONSOLE } from '../../../../constants/Roles';
import { SSO_INVITE_TYPE } from '../../utils';

const AddUser = ({ t, isOpen, onClose, onRefreshUsers, onOpenUpgradeDialog, isSsoAccount }) => {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState(null);
  const [role, setRole] = useState(0);
  const [roleError, setRoleError] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [totalUsers, setTotalUsers] = useState('');
  const [remainingUsers, setRemainingUsers] = useState('');
  const [allowedUpgrade, setAllowedUpgrade] = useState(false);
  const [declinedUpgrade, setDeclinedUpgrade] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [token, setToken] = useState();
  const [showDuplicateConfirmation, setShowDuplicateConfirmation] = useState(false);
  const [canInvitePartnerManager, setCanInvitePartnerManager] = useState(false);
  const [pro, setPro] = useState(false);

  const setEmailValue = (e) => {
    setErrorMessage(null);
    setEmail(e.target.value);
  };

  const getPlan = async () => {
    const userCount = await ProductSvc.getUserCount();
    const { planDetail } = await getAccountData();
    // https://confluence.endurance.com/pages/viewpage.action?pageId=71215428#ProductService-V2-CheckEntitlement
    const actions = userCount?.data?.actions;

    setAllowedUpgrade(actions && actions[0] === 'UPGRADE');

    const { total, remaining } = userCount?.data?.allowance || {};

    setTotalUsers(total);
    setRemainingUsers(remaining);
    setIsLoading(false);

    if (planDetail && planDetail.currentPlan.id === 193) {
      setPro(true);
      setRole(ACCOUNT_MANAGER);
    }
  };

  const validateCanInvitePartnerManager = async () => {
    if (!(await getPartnerOwnerSummary())) {
      setCanInvitePartnerManager(false);

      return;
    }

    const { enabled: localPartnerUIEnabled = false } = await SettingsSvc.getFeatureSetting(
      'bpconsole',
      'enable_partner_tab_override'
    );

    setCanInvitePartnerManager(localPartnerUIEnabled);
  };

  useEffect(() => {
    getPlan();
    validateCanInvitePartnerManager();
  }, []);

  useEffect(() => {
    // reset the state when closing.
    setRole(pro ? ACCOUNT_MANAGER : 0);
    setEmail('');
    setRoleError(false);
    setEmailError(null);
    setErrorMessage(null);
    setToken();
    setShowDuplicateConfirmation(false);
  }, [isOpen, pro]);

  const roleList = useMemo(() => {
    const roles = [
      {
        // ctct-ui-react List.items[].value asks for string
        value: `${ACCOUNT_MANAGER}`,
        heading: t('add_user.account_manager.title'),
        text: t('add_user.account_manager.description'),
      },
      {
        value: `${CAMPAIGN_CREATOR}`,
        heading: t('add_user.campaign_creator.title'),
        text: t('add_user.campaign_creator.description'),
      },
    ];

    if (canInvitePartnerManager) {
      roles.push({
        value: `${BP_CONSOLE}`,
        heading: t('add_user.bp_console.title'),
        text: t('add_user.bp_console.description'),
      });
    }

    return roles;
  }, [canInvitePartnerManager]);

  const doSendInvite = async () => {
    try {
      const invitationData = {
        target: email,
        targetType: 'EMAIL',
        roleId: role,
      };

      if (isSsoAccount) {
        invitationData.invitationType = SSO_INVITE_TYPE;
      }

      const invitation = await AccountSvc.postInvitation(invitationData);

      setToken(invitation.data.token);
    } catch (e) {
      let errorMessageLocal = t('add_user.error');

      if (e?.response?.status === 409) {
        errorMessageLocal = t('add_user.pending_invite_error');
      } else {
        const fieldErrors = e?.response?.data?.fieldErrors || null;

        if (fieldErrors && Array.isArray(fieldErrors) && fieldErrors.length > 0 && fieldErrors[0].errorType) {
          const [{ errorType }] = fieldErrors;

          if (errorType === 'DUPLICATE') {
            errorMessageLocal = t('add_user.duplicate_name_error');
          } else {
            errorMessageLocal = t('add_user.error_with_unknown_type', { errorType });
          }
        }
      }

      setErrorMessage(errorMessageLocal);
    }
  };

  const isDuplicateInvite = async () => {
    const getInvitationsReturn = await AccountSvc.getInvitations();
    const existingInvitations = getInvitationsReturn.data;

    // TODO: Filter by only invitations in pending status after CSI-4780 is done
    return existingInvitations.some((invitation) => invitation.target === email.trim());
  };

  const EMAIL_REGEX = /^(?!\.)(?!.*\.\.)([A-Z0-9_'+\-.]*)[A-Z0-9_+-]@([A-Z0-9][A-Z0-9-]*\.)+[A-Z]{2,}$/i;

  const onSendInvite = async () => {
    if (!email.trim()) {
      setEmailError('add_user.no_email_error');

      return;
    }

    if (!EMAIL_REGEX.test(email.trim())) {
      setEmailError('add_user.email_error');

      return;
    }

    setEmailError(null);

    if (!role) {
      setRoleError(true);

      return;
    }

    setRoleError(false);

    const duplicate = await isDuplicateInvite();

    if (!duplicate) {
      await doSendInvite();
    } else {
      setShowDuplicateConfirmation(true);
    }
  };

  const doSendAnyway = async () => {
    await AccountSvc.revokeInvitation(email.trim());
    await doSendInvite();
  };

  const goToManageUsers = () => {
    window.location.href = `https://app${getEnv()}constantcontact.com/pages/myaccount/side/users`;
  };

  if (token && isOpen) {
    return <InviteSentDialog onClose={onClose} t={t} email={email} isSsoAccount={isSsoAccount} />;
  }

  let actualRemainingUsers = remainingUsers;

  if (totalUsers === remainingUsers && totalUsers !== 0) {
    // For a newly provisioned user there is nothing that sets this count to 1 in backend
    // but the account owner already exists so count that user
    actualRemainingUsers = totalUsers - 1;
  }

  if (isLoading) {
    return null;
  }

  if (actualRemainingUsers === 0 && allowedUpgrade && isOpen && !declinedUpgrade) {
    // offer upgrade
    return (
      <UpgradeModal
        t={t}
        onClose={() => {
          setDeclinedUpgrade(true);
        }}
        onOpenUpgradeDialog={onOpenUpgradeDialog}
      />
    );
  }

  if (actualRemainingUsers === 0 && isOpen && (!allowedUpgrade || declinedUpgrade)) {
    // offer to remove a user
    return (
      <RemoveAUserFirstDialog
        t={t}
        onRefreshUsers={onRefreshUsers}
        onClose={onClose}
        doRefresh={() => {
          getPlan(); // to update the counts
          onRefreshUsers(); // to update the table and counts in outer page
        }}
      />
    );
  }

  return (
    <Dialog
      autoFocus={false}
      data-qe-id="add-user-dialog"
      heading={t(isSsoAccount ? 'add_user_sso.heading' : 'add_user.heading')}
      isOpen={isOpen}
      footerContent={
        isSsoAccount ? null : (
          <>
            {totalUsers > 0 ? (
              <span data-qe-id="users-count-text">
                {t('add_user.userspots', { available: actualRemainingUsers, allowed: totalUsers })}
              </span>
            ) : (
              ''
            )}
            {!pro && <Button onClick={goToManageUsers} text={t('add_user.manage_users')} kind="link" />}
          </>
        )
      }
      footerActions={
        <>
          <Button onClick={onClose} text={t('add_user.cancel')} kind="secondary" />
          <Button
            text={t('add_user.send_invite')}
            kind="primary"
            onClick={onSendInvite}
            data-qe-id="add-user-send-invite-btn"
          />
        </>
      }
      onDismiss={onClose}
    >
      {errorMessage && <Alert message={errorMessage} data-qe-id="add-user-error-alert" kind="error" />}
      {pro && (
        <div data-qe-id="add-user-pro-advice" style={{ textAlign: 'justify', fontWeight: 'bold', margin: '1rem 0rem' }}>
          {t('add_user.pro.description')}
        </div>
      )}
      {isSsoAccount && (
        <>
          <div
            data-qe-id="add-user-sso-description"
            style={{ textAlign: 'justify', fontWeight: '300', margin: '0rem 0rem 2rem 0rem' }}
          >
            {t('add_user_sso.description')}
          </div>
          {totalUsers > 0 && (
            <div
              data-qe-id="add-user-sso-userspots"
              style={{ textAlign: 'justify', fontWeight: '300', margin: '0rem 0rem 1.5rem 0rem' }}
            >
              <strong>
                {actualRemainingUsers} of {totalUsers}
              </strong>{' '}
              {t('add_user_sso.userspots_after')}{' '}
              <Button onClick={goToManageUsers} text={t('add_user.manage_users')} kind="link" />
            </div>
          )}
        </>
      )}
      <Field
        label={t('add_user.email_label')}
        required
        data-qe-id="add-user-email"
        error={emailError ? { errors: [t(emailError)], id: 'email-error' } : null}
      >
        <Input
          autoFocus
          data-qe-id="add-user-email-input"
          value={email}
          onChange={setEmailValue}
          placeholder={t('add_user.email_placeholder')}
        />
      </Field>
      {pro ? null : (
        <Field
          label={t(isSsoAccount ? 'add_user_sso.role_label' : 'add_user.role_label')}
          required
          data-qe-id="add-user-role"
          error={roleError ? { errors: [t('add_user.role_error')], id: 'role-error' } : null}
        >
          <List
            outlined
            id="radio-role"
            data-qe-id="roles-list"
            // ctct-ui-react List.value asks for string
            value={`${role}`}
            kind="radio"
            name="role_checkbox"
            onChoose={(e, data) => setRole(Number(data.item.value))}
            controlLocation="left"
            items={roleList}
          />
        </Field>
      )}

      {showDuplicateConfirmation && (
        <DuplicateConfirmation
          t={t}
          email={email}
          onOk={doSendAnyway}
          onCancel={() => setShowDuplicateConfirmation(false)}
        />
      )}
    </Dialog>
  );
};

AddUser.propTypes = {
  t: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onRefreshUsers: PropTypes.func.isRequired,
  onOpenUpgradeDialog: PropTypes.func.isRequired,
  isSsoAccount: PropTypes.bool,
};

AddUser.defaultProps = {
  isSsoAccount: false,
};

export default AddUser;
